import axios, { isAxiosError } from "axios";
import { ApiResponse, ResponseData } from "./Response";

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

export const post = async <T,>(url: string, body?: any, queryParams?: any): Promise<ApiResponse<T>> => {
  let result: ApiResponse<T>;

  try {
    const response = await instance.post<ResponseData<T>>(url, body, {
      params: queryParams,
    });
    result = {
      success: true,
      data: response.data.data,
      status: response.status,
    };
  } catch (error: Error | unknown) {
    if (isAxiosError<ResponseData<T>>(error)) {
      result = {
        success: false,
        status: error.response?.status,
        errors: error.response?.data?.errors || [{ title: "An error occurred" }]
      };
    } else {
      throw error;
    }
  }

  return result;
};

export const get = async <T,>(url: string, queryParams: any, config?: { headers?: Record<string, string> }): Promise<ApiResponse<T>> => {
  let result: ApiResponse<T>;

  try {
    const response = await instance.get<ResponseData<T>>(url, {
      params: queryParams,
      headers: config?.headers,
    });
    result = {
      success: true,
      data: response.data.data,
      status: response.status,
    };
  } catch (error: Error | unknown) {
    if (isAxiosError<ResponseData<T>>(error)) {
      result = {
        success: false,
        status: error.response?.status,
        errors: error.response?.data?.errors || [{ title: "An error occurred" }]
      };
    } else {
      throw error;
    }
  }

  return result;
};

export const getBlob = async (url: string): Promise<ApiResponse<Blob>> => {
  let result: ApiResponse<Blob>;

  try {
    const response = await instance.get<Blob>(url, {
      responseType: 'blob',
    });
    result = {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error: Error | unknown) {
    if (isAxiosError(error)) {
      result = {
        success: false,
        status: error.response?.status,
        errors: [{ title: "Failed to fetch file" }]
      };
    } else {
      throw error;
    }
  }

  return result;
};

export const remove = async <T,>(url: string, queryParams: any): Promise<ApiResponse<T>> => {
  let result: ApiResponse<T>;

  try {
    const response = await instance.delete<ResponseData<T>>(url, {
      params: queryParams,
    });
    result = {
      success: true,
      data: response.data.data,
      status: response.status,
    };
  } catch (error: Error | unknown) {
    if (isAxiosError<ResponseData<T>>(error)) {
      result = {
        success: false,
        status: error.response?.status,
        errors: error.response?.data?.errors || [{ title: "An error occurred" }]
      };
    } else {
      throw error;
    }
  }

  return result;
};

export const patch = async <T,>(url: string, body: any, queryParams: any): Promise<ApiResponse<T>> => {
  let result: ApiResponse<T>;

  try {
    const response = await instance.patch<ResponseData<T>>(url, body, {
      params: queryParams,
    });
    result = {
      success: true,
      data: response.data.data,
      status: response.status,
    };
  } catch (error: Error | unknown) {
    if (isAxiosError<ResponseData<T>>(error)) {
      result = {
        success: false,
        status: error.response?.status,
        errors: error.response?.data?.errors || [{ title: "An error occurred" }]
      };
    } else {
      throw error;
    }
  }

  return result;
};

export const put = async <T,>(url: string, body: any, queryParams?: any): Promise<ApiResponse<T>> => {
  let result: ApiResponse<T>;

  try {
    const response = await instance.put<ResponseData<T>>(url, body, {
      params: queryParams,
    });
    result = {
      success: true,
      data: response.data.data,
      status: response.status,
    };
  } catch (error: Error | unknown) {
    if (isAxiosError<ResponseData<T>>(error)) {
      result = {
        success: false,
        status: error.response?.status,
        errors: error.response?.data?.errors || [{ title: "An error occurred" }]
      };
    } else {
      throw error;
    }
  }

  return result;
};

export default instance;
